import { BoardNodeHandles } from "./board-node-handles";
import { NodeResizer, type NodeProps } from "@xyflow/react";
import { useCanvasEditability, useCanvasEditSession, useCanvasResizeSession } from "../../lib/canvas-editability";
import { patchNodeData } from "../../lib/canvas-commands";
import type { DiamondNodeData } from "../../types/board";
import { getContrastTextColor } from "../../lib/color-utils";

export function DiamondNode({ id, data, selected, isConnectable }: NodeProps) {
  const nodeData = data as unknown as DiamondNodeData;
  const { canEditDocument } = useCanvasEditability();
  const edit = useCanvasEditSession("text");
  const resize = useCanvasResizeSession();
  const updateLabel = (label: string) => {
    if (label !== (nodeData.label ?? "")) edit.update(patchNodeData(id, { label }));
  };

  const textColor = getContrastTextColor(nodeData.bgColor);
  const fontSize = nodeData.fontSize || 14;

  return (
    <>
      <NodeResizer
        {...resize}
        isVisible={!!selected && canEditDocument}
        minWidth={80}
        minHeight={80}
        keepAspectRatio
        lineClassName="!border-primary"
        handleClassName="!h-2.5 !w-2.5 !rounded-sm !border-2 !border-primary !bg-background"
      />
      <BoardNodeHandles type="diamond" isConnectable={isConnectable} />
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
      >
        <polygon
          points="50,2 98,50 50,98 2,50"
          fill={nodeData.bgColor || "var(--color-card)"}
          stroke={nodeData.borderColor || "var(--color-border)"}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ color: textColor, fontSize }}
        onDoubleClick={edit.begin}
      >
        {edit.active ? (
          <input
            autoFocus
            className="nodrag nowheel w-16 bg-transparent text-center outline-none"
            style={{ color: textColor, fontSize }}
            defaultValue={nodeData.label}
            onBlur={(e) => {
              updateLabel(e.target.value);
              edit.finish();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                updateLabel((e.target as HTMLInputElement).value);
                edit.finish();
              }
            }}
          />
        ) : (
          <span className="min-h-0 min-w-0 max-h-[48%] w-[48%] overflow-hidden whitespace-pre-wrap text-center [overflow-wrap:anywhere]">{nodeData.label ?? ""}</span>
        )}
      </div>
    </>
  );
}
